<script setup lang="ts">
// import { useI18nRoute } from '@/composables/useI18nRoute'
import { computed } from 'vue'
import { localizedPath } from '@/router/i18nPath'
import { localizedPagePath } from '@/router/pageSlugs'
import { withUtmParams } from '@/utils/utm'

import { useI18n } from 'vue-i18n'
const { t } = useI18n()

function footerHref(href: string, content?: string) {
  return withUtmParams(href, {
    utm_source: 'footer_navigation',
    utm_medium: 'webpage',
    utm_campaign: 'site_navigation',
    ...(content ? { utm_content: content } : {}),
  })
}

const currentYear = computed(() => new Date().getFullYear())
const buildVersion = import.meta.env.VITE_APP_BUILD_VERSION?.trim() ?? ''

</script>

<template>
  <footer itemscope itemtype="https://schema.org/WPFooter"
    class="footer-btm relative self-end w-full px-6 py-12 lg:py-16 xl:py-32 bg-surface text-base-content">


    <div class="footer-content w-full flex flex-col lg:flex-row lg:flex-wrap lg:gap-8 mx-auto max-w-screen-2xl">

      <div class="w-full lg:flex-1 lg:basis-col-2 order-1 lg:order-0 space-y-4 relative mt-8 lg:mt-0">

        <div class="max-w-prose space-y-2">
          <h2 itemprop="headline" class="font-bold text-lg text-primary-deep">{{ currentYear }} &copy; FitnessRoom</h2>
          <p itemprop="text" class="text-base">{{ t('footer.companyGroup') }}</p>
        </div>

      </div>

      <div class="w-full lg:flex-1 lg:basis-col-2 md:flex md-flex-row md:gap-4 flex-wrap space-y-8 md:space-y-0">

        <div class="md:w-1/3">

          <nav class="flex-1">
            <ul class="space-y-2">
              <li>
                <h2 class="font-bold text-lg text-primary-deep">{{ t('footer.gyms') }}</h2>
              </li>

              <li>
                <RouterLink 
                  class="text-foreground/90 hover:text-primary-deep" 
                  aria-label="FitnessRoom Kværnerbyen" 
                  :to="localizedPath('gym', { slug: 'kvaernerbyen' })"
                >
                  Kværnerbyen
                </RouterLink>
              </li>

              <li>
                <RouterLink 
                  class="text-foreground/90 hover:text-primary-deep" 
                  aria-label="FitnessRoom Rykkinn" 
                  :to="localizedPath('gym', { slug: 'rykkinn' })"
                >
                  Rykkinn
                </RouterLink>
              </li>

              <li>
                <RouterLink 
                  class="text-foreground/90 hover:text-primary-deep" 
                  aria-label="FitnessRoom Sandvika" 
                  :to="localizedPath('gym', { slug: 'sandvika' })"
                >
                  Sandvika
                </RouterLink>
              </li>

            </ul>
          </nav>

        </div>

        <div class="md:w-1/3">

          <nav class="flex-1">
            <ul class="space-y-2">
              <li>
                <h2 class="font-bold text-lg text-primary-deep">{{ t('footer.help') }}</h2>
              </li>
              <li>
                <RouterLink 
                  class="text-foreground/90 hover:text-primary-deep" 
                  :aria-label="t('footer.customerService')" 
                  :to="localizedPagePath('customerService')"
                >
                    {{ t('footer.customerService') }}
                </RouterLink>
              </li>

              <li>
                <RouterLink 
                  class="text-foreground/90 hover:text-primary-deep" 
                  :aria-label="t('footer.about')" 
                  :to="localizedPagePath('about')"
                >
                  {{ t('footer.about') }}
                </RouterLink>
              </li>

              <li>
                <RouterLink 
                  class="text-foreground/90 hover:text-primary-deep" 
                  :aria-label="t('footer.membershipTerms')" 
                  :to="localizedPagePath('membershipTerms')"
                >
                  {{ t('footer.membershipTerms') }}
                </RouterLink>
              </li>


              <li>
                <RouterLink 
                  class="text-foreground/90 hover:text-primary-deep" 
                  :aria-label="t('footer.privacy')" 
                  :to="localizedPagePath('privacy')"
                >
                  {{ t('footer.privacy') }}
                </RouterLink>
              </li>

            </ul>
          </nav>


        </div>

        <div class="md:w-1/3 -order-1 lg:order-none lg:flex-1">

          <nav class="flex-1">
            <ul class="space-y-2">
              <li>
                <h2 class="font-bold text-lg text-primary-deep">{{ t('footer.social') }}</h2>
              </li>
              <li>
                <a 
                  class="text-foreground/90 hover:text-primary-deep" 
                  aria-label="Facebook" 
                  :href="footerHref('https://facebook.com/FitnessRoomSandvika/', 'facebook')"
                  rel="nofollow" 
                  target="_blank"
                >
                  Facebook
                </a>
              </li>
              <li>
                <a 
                  class="text-foreground/90 hover:text-primary-deep" 
                  aria-label="Instagram" 
                  :href="footerHref('https://www.instagram.com/fitnessroom.no/', 'instagram')"
                  rel="nofollow" 
                  target="_blank"
                >
                  Instagram
                </a>
              </li>
              <li>
                <a 
                  class="text-foreground/90 hover:text-primary-deep" 
                  aria-label="YouTube" 
                  :href="footerHref('https://www.youtube.com/@FitnessRoomNorway', 'youtube')"
                  rel="nofollow" 
                  target="_blank"
                >
                  YouTube
                </a>
              </li>

            </ul>
          </nav>

        </div>

      </div>

      <aside class="basis-full order-last mt-8 flex justify-center lg:justify-center">
        <p v-if="buildVersion" class="text-xs text-muted-foreground">
          Build {{ buildVersion }}
        </p>
      </aside>

    </div>

  </footer>
</template>
